import api from './api';
import { Customer } from './customers';
import { Business } from './businesses';
import { Team } from './teams';
import { User } from './auth';
import { Subject } from './subjects';
import { Staff } from './staffs';

const unwrap = <T>(response: any): T => response.data?.data ?? response.data;

export interface LetterSubTask {
  id: number;
  letter_id: number;
  type: string;
  name: string;
  completed: boolean;
  created_at: string;
  updated_at: string;
}

export interface LetterItem {
  id: number;
  letter_id: number;
  attachment?: string;
  status?: string;
  enabled: boolean;
  created_at: string;
  updated_at: string;
  creator?: User;
}

export interface LetterComment {
  id: number;
  letter_id: number;
  user_id?: number;
  comment: string;
  created_at: string;
  user?: User;
}

export interface LetterReview {
  id: number;
  letter_id: number;
  customer_id?: number;
  review: string;
  created_at: string;
  customer?: Customer;
  creator?: User;
}

export interface Letter {
  id: number;
  type?: 'personal' | 'business';
  reference_id?: number;
  customer_id?: number;
  business_id?: number;
  sender_id?: number;
  subject_id?: number;
  create_date?: string;
  due_date?: string;
  team_id?: number;
  status: string;
  is_sent?: boolean;
  enabled: boolean;
  created_by?: number;
  updated_by?: number;
  created_at: string;
  updated_at: string;
  customer?: Customer;
  business?: Business;
  sender?: Staff;
  subject?: Subject;
  team?: Team;
  subTasks?: LetterSubTask[];
  items?: LetterItem[];
  comments?: LetterComment[];
  reviews?: LetterReview[];
}

export interface CreateLetterData {
  type?: 'personal' | 'business';
  reference_id?: number;
  customer_id?: number;
  business_id?: number;
  sender_id?: number;
  subject_id?: number;
  create_date?: string;
  due_date?: string;
  team_id?: number;
  status?: string;
  is_sent?: boolean;
  enabled?: boolean;
  sub_tasks?: Array<{
    name: string;
    completed?: boolean;
  }>;
}

export interface CreateLetterItemData {
  letter_id: number;
  attachment?: File;
  status?: string;
  enabled?: boolean;
}

export interface CreateLetterCommentData {
  letter_id: number;
  comment: string;
}

export interface CreateLetterReviewData {
  letter_id: number;
  customer_id?: number;
  review: string;
}

export const letterService = {
  async getAll() {
    const response = await api.get('/letters');
    return response.data;
  },

  async getById(id: number): Promise<Letter> {
    const response = await api.get(`/letters/${id}`);
    return unwrap<Letter>(response);
  },

  async create(data: CreateLetterData): Promise<Letter> {
    const response = await api.post('/letters', data);
    return unwrap<Letter>(response);
  },

  async update(id: number, data: Partial<CreateLetterData>): Promise<Letter> {
    const response = await api.put(`/letters/${id}`, data);
    return unwrap<Letter>(response);
  },

  async delete(id: number): Promise<void> {
    await api.delete(`/letters/${id}`);
  },
};

export const letterItemService = {
  async create(data: CreateLetterItemData): Promise<LetterItem> {
    const formData = new FormData();
    formData.append('letter_id', data.letter_id.toString());
    if (data.attachment) {
      formData.append('attachment', data.attachment);
    }
    if (data.status) {
      formData.append('status', data.status);
    }
    formData.append('enabled', (data.enabled ?? true).toString());
    
    const response = await api.post('/letter-items', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return unwrap<LetterItem>(response);
  },

  async delete(id: number): Promise<void> {
    await api.delete(`/letter-items/${id}`);
  },

  async view(id: number): Promise<Blob> {
    const response = await api.get(`/letter-items/${id}/view`, {
      responseType: 'blob',
    });
    return response.data;
  },

  async download(id: number): Promise<Blob> {
    const response = await api.get(`/letter-items/${id}/download`, {
      responseType: 'blob',
    });
    return response.data;
  },
};

export const letterCommentService = {
  async create(data: CreateLetterCommentData): Promise<LetterComment> {
    const response = await api.post('/letter-comments', data);
    return unwrap<LetterComment>(response);
  },
  async delete(id: number): Promise<void> {
    await api.delete(`/letter-comments/${id}`);
  }
};

export const letterReviewService = {
  async create(data: CreateLetterReviewData): Promise<LetterReview> {
    const response = await api.post('/letter-reviews', data);
    return unwrap<LetterReview>(response);
  }
};
